/**
 * AreaOfOperationPicker - Wrapper around generic EntityPicker
 * 
 * Provides area-of-operation-specific configuration and maintains backward compatibility
 * with any code that imports AreaOfOperationPickerField or AreaOfOperationPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── AreaOfOperation-specific configuration ───────────────────────────────────
const AREAOFOPERATION_CONFIG = {
	apiEndpoint: "/api/AreaOfOperation",
	entityName: "Area of Operation",
	idField: "id",
	displayFields: {
		primary: "description",
		secondary: "code"
	},
	tableHeaders: ["Code", "Description"],
	tableAccessors: ["code", "description"],
	searchFields: [
		{ label: "All", field: "all", index: 0 },
		{ label: "Description", field: "description", index: 1 },
		{ label: "Code", field: "code", index: 2 },
	],
	placeholder: "--Select area of operation--"
};

// ── AreaOfOperationPickerField wrapper ───────────────────────────────────────
/**
 * AreaOfOperationPickerField
 * 
 * Usage:
 * <AreaOfOperationPickerField
 *   label="Area of Operation"
 *   areaOfOperationId={form.areaOfOperationId}
 *   displayValue={form.areaOfOperationDescription}
 *   onSelect={(area) => {
 *     set("areaOfOperationId", area.id);
 *     set("areaOfOperationCode", area.code);
 *     set("areaOfOperationDescription", area.description);
 *   }}
 *   onClear={() => {
 *     set("areaOfOperationId", "");
 *     set("areaOfOperationCode", "");
 *     set("areaOfOperationDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function AreaOfOperationPickerField({
	areaOfOperationId,  // area-of-operation-specific prop name
	displayValue = "",
	onSelect,
	onClear,
	...rest             // label, isRequired, error, disabled, etc.
}) {
	return (
		<EntityPickerField
			{...rest}
			config={AREAOFOPERATION_CONFIG}
			entityId={areaOfOperationId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── AreaOfOperationPickerModal wrapper ───────────────────────────────────────
/**
 * AreaOfOperationPickerModal
 * 
 * Usage (standalone):
 * <AreaOfOperationPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(area) => {
 *     set("areaOfOperationId", area.id);
 *     set("areaOfOperationDescription", area.description);
 *   }}
 * />
 */
export function AreaOfOperationPickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={AREAOFOPERATION_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { AreaOfOperationPickerField as default };